import type {HeuristicRegistry} from '../mobileAlgo'
import type {
    BaseStageData,
    ComponentStageData,
    InnerElementStageData,
    PluginContextHelper,
    PluginHeuristicsRegistry,
    PluginInnerElementStageHandler,
    PluginPostStageHandler,
    PluginStageHandler,
    Stage
} from '../types'
import {createPluginContext} from './pluginContext'

export const createPluginHeuristicsRegistry = (
    heuristicRegistryApi: HeuristicRegistry,
    contextHelper: PluginContextHelper
): PluginHeuristicsRegistry => {
    const register = <T extends BaseStageData>(stage: Stage<T>, handler: PluginStageHandler<T>) => {
        const handlerToRegister = async (d: T) => {
            try {
                if (contextHelper.isEnabled(d.globalContext)) {
                    await handler(d, createPluginContext(contextHelper, d.globalContext))
                }
            } catch (e: any) {
                e.extras = {
                    ...e.extras,
                    handlerType: 'StageHandler',
                    plugin: contextHelper.name
                }
                throw e
            }
        }
        heuristicRegistryApi.registerHeuristic(stage, handlerToRegister)
    }

    const registerInnerElementHandler = <T extends ComponentStageData>(
        stage: Stage<T>,
        handler: PluginInnerElementStageHandler
    ) => {
        if (!stage.registerInnerElementHandler) {
            throw new Error('Stage does not support inner element handlers')
        }

        const handlerToRegister = async (data: InnerElementStageData) => {
            try {
                if (contextHelper.isEnabled(data.globalContext)) {
                    await handler(data, createPluginContext(contextHelper, data.globalContext))
                }
            } catch (e: any) {
                e.extras = {
                    ...e.extras,
                    handlerType: 'InnerElementHandler',
                    plugin: contextHelper.name
                }
                throw e
            }
        }

        heuristicRegistryApi.registerInnerElementHeuristic(stage, handlerToRegister)
    }

    const registerPostStageOperation = <T extends BaseStageData>(stage: Stage<T>, handler: PluginPostStageHandler) => {
        if (!stage.registerPostStageOperation) {
            throw new Error('Stage does not support post stage handlers')
        }

        const handlerToRegister = async (d: BaseStageData) => {
            try {
                if (contextHelper.isEnabled(d.globalContext)) {
                    await handler(d, createPluginContext(contextHelper, d.globalContext))
                }
            } catch (e: any) {
                e.extras = {
                    ...e.extras,
                    handlerType: 'PostStageOperation',
                    plugin: contextHelper.name
                }
                throw e
            }
        }

        heuristicRegistryApi.registerPostStageOperation(stage, handlerToRegister)
    }

    return {
        register,
        registerInnerElementHandler,
        registerPostStageOperation
    }
}
